import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card, PageHeader, Spinner, fmtEur, Button } from "@/components/common";
import {
  Users, Euro, HeartPulse, TrendingUp, TrendingDown, Wallet, Dumbbell,
  IdCard, ClipboardCheck, ArrowRight,
} from "lucide-react";
import { AreaChart, Area, ResponsiveContainer, Tooltip, XAxis } from "recharts";
import { PayPalPay } from "@/components/PayPalPay";

const attData = [
  { m: "Gen", v: 78 }, { m: "Feb", v: 82 }, { m: "Mar", v: 85 },
  { m: "Apr", v: 80 }, { m: "Mag", v: 88 }, { m: "Giu", v: 92 },
];

function Kpi({ icon: Icon, label, value, sub, tone = "slate", testid }) {
  const tones = {
    slate: "bg-slate-900", sky: "bg-sky-600", emerald: "bg-emerald-600",
    amber: "bg-amber-500", red: "bg-red-600",
  };
  return (
    <Card className="p-5" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs uppercase tracking-wider font-semibold text-slate-400">{label}</p>
          <p className="text-2xl font-bold font-display text-slate-900 mt-1.5">{value}</p>
          {sub && <p className="text-xs text-slate-400 mt-1">{sub}</p>}
        </div>
        <div className={`w-10 h-10 rounded-xl ${tones[tone]} grid place-items-center`}><Icon size={19} className="text-white" /></div>
      </div>
    </Card>
  );
}

export default function Dashboard() {
  const { user, tenant, refresh } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [pp, setPp] = useState(null);
  const trialDays = tenant?.trial_ends_at ? Math.max(0, Math.ceil((new Date(tenant.trial_ends_at) - new Date()) / 86400000)) : 0;

  useEffect(() => {
    if (["atleta", "genitore"].includes(user?.role)) { navigate("/app/portale"); return; }
    api.get("/dashboard").then((r) => setData(r.data)).catch(() => setData({}));
  }, [user, navigate]);

  if (!data) return <Spinner />;
  const c = data.certificati || { green: 0, orange: 0, red: 0 };

  return (
    <div>
      <PageHeader title={`Ciao, ${user?.name?.split(" ")[0]} 👋`} subtitle={tenant ? tenant.name : "Panoramica piattaforma"} />

      {user?.role === "admin_asd" && tenant && tenant.plan_status !== "active" && (
        <Card className="p-5 mb-5 border-sky-200 bg-sky-50/70 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid="trial-banner">
          <div>
            <p className="font-semibold font-display text-slate-900">{trialDays > 0 ? `Prova gratuita: ${trialDays} giorni rimanenti` : "Prova gratuita terminata"}</p>
            <p className="text-sm text-slate-500">Attiva il piano {tenant.plan} con PayPal per continuare senza interruzioni.</p>
          </div>
          <Button variant="accent" onClick={() => setPp({ kind: "subscription", plan: tenant.plan, label: `Abbonamento ${tenant.plan}` })} data-testid="upgrade-plan-btn">Attiva con PayPal</Button>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
        <Kpi testid="dashboard-kpi-crediti" icon={Euro} label="Crediti da incassare" value={fmtEur(data.crediti_da_incassare)} sub="Rate non saldate" tone="amber" />
        <Kpi testid="dashboard-kpi-saldo" icon={Wallet} label="Saldo economico" value={fmtEur(data.saldo)} sub={`Entrate ${fmtEur(data.entrate)}`} tone="emerald" />
        <Kpi testid="dashboard-kpi-atleti" icon={Users} label="Tesserati / Atleti" value={data.n_atleti} sub={`${data.n_persone} persone totali`} tone="sky" />
        <Kpi testid="dashboard-kpi-presenze" icon={ClipboardCheck} label="Tasso presenze" value={`${data.attendance_rate}%`} sub="Media stagionale" tone="slate" />
      </div>

      <div className="grid lg:grid-cols-3 gap-5 mt-5">
        {/* Certificati semaforo */}
        <Card className="p-6 lg:col-span-1" data-testid="dashboard-certificati">
          <div className="flex items-center gap-2 mb-4"><HeartPulse size={18} className="text-slate-700" /><h3 className="font-semibold font-display text-slate-800">Certificati medici</h3></div>
          {[["green", "Validi", c.green, "bg-[#DCFCE7] text-[#15803D]"], ["orange", "In scadenza < 30gg", c.orange, "bg-[#FFEDD5] text-[#C2410C]"], ["red", "Scaduti", c.red, "bg-[#FEE2E2] text-[#B91C1C]"]].map(([k, l, v, cls]) => (
            <div key={k} className="flex items-center justify-between py-2.5 border-b border-slate-100 last:border-0">
              <span className="flex items-center gap-2.5 text-sm text-slate-600"><span className={`w-2.5 h-2.5 rounded-full ${k === "green" ? "bg-emerald-500" : k === "orange" ? "bg-amber-500" : "bg-red-500"}`} />{l}</span>
              <span className={`px-2.5 py-0.5 rounded-full text-sm font-bold ${cls}`}>{v}</span>
            </div>
          ))}
          <Button variant="outline" size="sm" className="w-full mt-4" onClick={() => navigate("/app/certificati")} data-testid="dashboard-goto-certificati">Gestisci certificati <ArrowRight size={15} /></Button>
        </Card>

        {/* Presenze chart */}
        <Card className="p-6 lg:col-span-2" data-testid="dashboard-presenze-chart">
          <div className="flex items-center gap-2 mb-4"><TrendingUp size={18} className="text-slate-700" /><h3 className="font-semibold font-display text-slate-800">Andamento presenze</h3></div>
          <ResponsiveContainer width="100%" height={200}>
            <AreaChart data={attData} margin={{ left: 0, right: 0, top: 5, bottom: 0 }}>
              <defs><linearGradient id="g" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#0284C7" stopOpacity={0.35} /><stop offset="100%" stopColor="#0284C7" stopOpacity={0} /></linearGradient></defs>
              <XAxis dataKey="m" tickLine={false} axisLine={false} tick={{ fontSize: 12, fill: "#94A3B8" }} />
              <Tooltip contentStyle={{ borderRadius: 10, border: "1px solid #E2E8F0", fontSize: 13 }} formatter={(v) => [`${v}%`, "Presenze"]} />
              <Area type="monotone" dataKey="v" stroke="#0284C7" strokeWidth={2.5} fill="url(#g)" />
            </AreaChart>
          </ResponsiveContainer>
        </Card>
      </div>

      {/* Entrate/Uscite + quick */}
      <div className="grid lg:grid-cols-3 gap-5 mt-5">
        <Card className="p-6" data-testid="dashboard-entrate">
          <div className="flex items-center gap-2 mb-1"><TrendingUp size={18} className="text-emerald-600" /><span className="text-sm text-slate-500">Entrate totali</span></div>
          <p className="text-2xl font-bold font-display text-slate-900">{fmtEur(data.entrate)}</p>
        </Card>
        <Card className="p-6" data-testid="dashboard-uscite">
          <div className="flex items-center gap-2 mb-1"><TrendingDown size={18} className="text-red-500" /><span className="text-sm text-slate-500">Uscite totali</span></div>
          <p className="text-2xl font-bold font-display text-slate-900">{fmtEur(data.uscite)}</p>
        </Card>
        <Card className="p-6" data-testid="dashboard-corsi">
          <div className="flex items-center gap-2 mb-1"><Dumbbell size={18} className="text-sky-600" /><span className="text-sm text-slate-500">Corsi e squadre attive</span></div>
          <p className="text-2xl font-bold font-display text-slate-900">{data.n_corsi}</p>
        </Card>
      </div>
      <PayPalPay open={!!pp} order={pp || {}} onClose={() => setPp(null)} onSuccess={refresh} />
    </div>
  );
}
